import React, { useEffect, useRef, useState } from "react";
import type { BreakdownMode } from "./api";
import { PlanFieldItem, YearCard } from "./year-card";

type AddPlanAccordionProps = {
  fields: PlanFieldItem[];
  remove: (index: number) => void;
  resetKey?: number;
  selectedBreakdownMode: BreakdownMode;
};

export const AddPlanAccordion = (props: AddPlanAccordionProps) => {
  const { fields, remove, resetKey = 0, selectedBreakdownMode } = props;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(fields.length > 0 ? [fields[0].id] : []));
  const [lastEndDates, setLastEndDates] = useState<Record<number, string>>({});
  const didInitLastEndDates = useRef(false);

  useEffect(() => {
    if (resetKey === 0) return;
    setLastEndDates({});
    didInitLastEndDates.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (didInitLastEndDates.current || fields.length === 0) return;

    const initial: Record<number, string> = {};

    fields.forEach((item, index) => {
      const lastRow = item.billingScheduleRows?.at(-1);
      if (lastRow?.endDate) initial[index] = lastRow.endDate;
    });

    if (Object.keys(initial).length > 0) {
      setLastEndDates(initial);
      didInitLastEndDates.current = true;
    }
  }, [fields]);

  const prevLengthRef = useRef(fields.length);

  useEffect(() => {
    const added = fields.length - prevLengthRef.current;
    if (added === 1) {
      const newField = fields[fields.length - 1];
      setExpandedIds((prev) => {
        const next = new Set(Array.from(prev));
        next.add(newField.id);
        return next;
      });
    }
    prevLengthRef.current = fields.length;
  }, [fields.length, fields]);

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      {fields.map((item, index) => (
        <YearCard
          key={`${item.id}-${resetKey}`}
          planIndex={index}
          item={item}
          resetKey={resetKey}
          isExpanded={expandedIds.has(item.id)}
          onToggle={() => toggle(item.id)}
          onRemove={() => remove(index)}
          lastEndDate={lastEndDates[index - 1]}
          onBreakdownGenerated={(endDate) => setLastEndDates((prev) => ({ ...prev, [index]: endDate }))}
          selectedBreakdownMode={selectedBreakdownMode}
        />
      ))}
    </>
  );
};
